import React, { useEffect, useMemo, useState } from "react";
import MyTable from "../ui/Table";
import { Dropdown as AntdDropdown, Button, Menu, Input, Space } from "antd";
import {
  CopyOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import apiRoutes from "../api/apiRoutes";
import { apiGet, apiPost } from "../api/request";
import AddQuotesModal from "../modals/AddQuotesModal";
import GlowButton from "../components/GlowButton";

const DailyQuotes = () => {
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formData, setFormData] = useState({
    book_id: "",
    module_id: "",
    quote: "",
  });
  const [dropdownOptions, setDropdownOptions] = useState({
    books: [],
    modules: [],
  });
  const [quotes, setQuotes] = useState([]);

  // Show/hide modal
  const showModal = () => setIsModalVisible(true);
  const hideModal = () => {
    setFormData({ book_id: "", module_id: "", quote: "" });
    setIsModalVisible(false);
  };

  // Fetch books on mount
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const res = await apiGet(apiRoutes.getEbooks);
        const books = Array.isArray(res.responsedata)
          ? res.responsedata.map((book) => ({
              label: book.title,
              value: book.id,
            }))
          : [];

        setDropdownOptions((prev) => ({ ...prev, books }));
      } catch (err) {
        console.error("Failed to fetch books:", err);
      }
    };
    fetchBooks();
    fetchQuotes();
  }, []);

  // Fetch modules for selected book
  const onBookChange = async (bookId) => {
    try {
      const res = await apiGet(apiRoutes.getBookContents(bookId));

      const modules = Array.isArray(res)
        ? res.map((mod) => ({
            label: mod.title,
            value: mod.id,
          }))
        : [];
      setDropdownOptions((prev) => ({ ...prev, modules }));
    } catch (err) {
      console.error("Failed to fetch modules:", err);
    }
  };

  // Submit quote
  const handleSubmit = async () => {
    try {
      const payload = {
        bookid: formData.book_id,
        moduleid: formData.module_id,
        quote: formData.quote,
        created_by_id: 1,
      };
      console.log("Quote data:", payload);
      const res = await apiPost(apiRoutes.createQuote, payload);
      console.log("Quote created:", res);
      hideModal();
      fetchQuotes();
    } catch (err) {
      console.error("Failed to create quote:", err);
    }
  };

  // Fetch existing quotes (for table)
  const fetchQuotes = async () => {
    try {
      const res = await apiGet(apiRoutes.getAllQuotes);
      if (Array.isArray(res.responsedata)) {
        setQuotes(res.responsedata);
      }
    } catch (err) {
      console.error("Failed to fetch quotes:", err);
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const filteredData = useMemo(() => {
    return quotes.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchText.toLowerCase()),
      ),
    );
  }, [searchText, quotes]);

  const handleTitleClick = (record) => {
    navigate(`/managebook?id=${record.book_id}`);
  };

  const handleMenuClick = (record, action) => {
    if (action === "Delete") {
      // navigate(`/bookdetails?id=${record.book_id}`);
      // } else if (action === "edit") {
      //   navigate(`/managebook?id=${record.book_id}`);
      // } else if (action === "clone") {
      //   console.log("Cloning:", record);
    }
  };

  const columns = [
    {
      title: "Quote",
      dataIndex: "quote",
      key: "quote",
    },
    {
      title: "Book",
      dataIndex: "book_title",
      key: "book_title",
      render: (_, record) => (
        <span
          onClick={() => handleTitleClick(record)}
          style={{ color: "black", cursor: "pointer" }}
        >
          {record.EbookCover ? record.EbookCover.title : "N/A"}
        </span>
      ),
    },
    {
      title: "Module",
      dataIndex: "module_title",
      key: "module_title",
      render: (
        _,
        record, // Use render to access nested data Access nested title from EbookContent object
      ) => (record.EbookContent ? record.EbookContent.title : "N/A"),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "date",
      render: (text) => {
        if (!text) return null;
        const date = new Date(text);
        return date.toLocaleDateString("en-GB");
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <AntdDropdown
          overlay={
            <Menu
              onClick={({ key }) => handleMenuClick(record, key)}
              items={[
                { label: "Edit", key: "edit", icon: <EditOutlined /> },
                { label: "View", key: "view", icon: <EyeOutlined /> },
              ]}
            />
          }
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </AntdDropdown>
      ),
    },
  ];

  return (
    <div>
      <h1 className="text-xl font-semibold mb-4">Manage Daily Quotes</h1>

      <div className="border border-gray-300 rounded-md">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 p-4">
          <Space className="mb-2 md:mb-0">
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              className="w-[250px]"
            />
          </Space>
          <GlowButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={showModal}
            style={{ backgroundColor: "black", borderColor: "black" }}
          >
            + Create Quote
          </GlowButton>
        </div>
        <div className="border border-gray-200 rounded-md">
          <MyTable columns={columns} dataSource={filteredData} />
        </div>
      </div>

      <AddQuotesModal
        visible={isModalVisible}
        onCancel={hideModal}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        dropdownOptions={dropdownOptions}
        onBookChange={onBookChange}
      />
    </div>
  );
};

export default DailyQuotes;
